import { Controller } from "@hotwired/stimulus"
import { glide } from "../libs/motion"

export default class extends Controller {
  static targets = ["viewport", "track", "item", "prev", "next"]

  static values = {
    itemsToShow: { type: Number, default: 4 },
    itemsToShowBreakpoints: { type: String,  default: "" },
    gap: { type: Number, default: 16 },
    minWidth: { type: Number, default: 280 }
  }

  connect () {
    this.breakpoints = this.parseBreakpoints()
    this.index = 0

    // ResizeObserver keeps everything in sync with the viewport
    this.ro = new ResizeObserver(() => this.layout())
    this.ro.observe(this.viewportTarget)

    this.viewportTarget.style.overflowX = 'auto'
    this.viewportTarget.style.scrollbarWidth = 'none' // Firefox
    this.viewportTarget.style.msOverflowStyle = 'none' // IE/Edge
    this.viewportTarget.style.setProperty('scrollbar-width', 'none')
    
    this.viewportTarget.addEventListener('scroll', this.handleScroll.bind(this))

    this.layout()
  }

  disconnect () { 
    this.ro?.disconnect()
    this.viewportTarget?.removeEventListener('scroll', this.handleScroll.bind(this))
  }

  slideLeft  () { 
    if (this.index > 0) {
      const moveBy = this.pageSize
      this.index = Math.max(0, this.index - moveBy)
      this.scrollToIndex()
    }
  }

  slideRight () {
    const max = Math.max(0, this.itemTargets.length - this.visibleCount)
    if (this.index < max) {
      const moveBy = this.pageSize
      this.index = Math.min(max, this.index + moveBy)
      this.scrollToIndex()
    }
  }

  // How far an arrow moves: all but one of the cards in view, so one stays for
  // context; one at a time when only one is in view whole (a narrow screen's 1.5).
  get pageSize () {
    const whole = Math.floor(this.visibleCount)
    return whole < 2 ? 1 : Math.max(2, whole - 1)
  }

  parseBreakpoints () {
    if (!this.hasItemsToShowBreakpointsValue) return []

    return this.itemsToShowBreakpointsValue
      .split(",")
      .map(p => {
        const [w, n] = p.split(":").map(Number)
        return { min: w, items: n }
      })
      .sort((a, b) => a.min - b.min)
  }

  // Resolve the number of items to show based on the viewport width.
  // Always use the configured column count for sizing so cards maintain
  // consistent dimensions even when fewer items are present.
  resolveVisibleCount () {
    let n = this.itemsToShowValue
    const vw = window.innerWidth

    for (const bp of this.breakpoints)
      if (vw >= bp.min) n = bp.items

    return Math.max(1, n)
  }

  // Calculate the width of each item and the total width of the track
  layout () {
    this.visibleCount = this.resolveVisibleCount()

    const { width } = this.viewportTarget.getBoundingClientRect()
    const gap  = this.gapValue
    const n    = this.visibleCount
    
    const availableWidth = width - (n - 1) * gap
    const calculatedWidth = availableWidth / n
    
    this.itemWidth = Math.max(calculatedWidth, this.minWidthValue)
    
    if (this.itemWidth * n + (n - 1) * gap > width) {
      this.itemWidth = (width - (n - 1) * gap) / n
    }

    const trackW    = this.itemTargets.length * this.itemWidth +
                      (this.itemTargets.length - 1) * gap

    this.trackTarget.style.width = `${trackW}px`

    this.itemTargets.forEach((el, i) => {
      el.style.width       = `${this.itemWidth}px`
      el.style.marginRight = (i === this.itemTargets.length - 1)
                               ? 0 : `${gap}px`
    })

    const maxIndex = Math.max(0, this.itemTargets.length - n)
    if (this.index > maxIndex) this.index = maxIndex
    this.updateButtonStates()
  }

  scrollToIndex () {
    glide(this.viewportTarget, this.index * (this.itemWidth + this.gapValue))
  }

  handleScroll () {
    const scrollLeft = this.viewportTarget.scrollLeft
    const gap = this.gapValue
    
    this.index = Math.round(scrollLeft / (this.itemWidth + gap))
    this.index = Math.max(0, Math.min(this.index, Math.max(0, this.itemTargets.length - this.visibleCount)))
    
    this.updateButtonStates()
  }

  updateButtonStates () {
    const maxIndex = Math.max(0, this.itemTargets.length - this.visibleCount)
    const scrollLeft = this.viewportTarget.scrollLeft
    const maxScrollLeft = this.viewportTarget.scrollWidth - this.viewportTarget.clientWidth
    
    this.disable(this.prevTarget, scrollLeft <= 0)
    this.disable(this.nextTarget, scrollLeft >= maxScrollLeft || this.index >= maxIndex)
  }

  // The button component shows its disabled look from aria-disabled.
  disable(button, disabled) {
    button.disabled = disabled
    button.setAttribute("aria-disabled", disabled)
  }
}
